import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

// Shared search filters that live above the router: today that is the budget
// range picked from the nav search pill. Every surface that lists homes
// (landing deck, landing grid, /map pins and deck, /swipe deck) reads from
// here so one choice filters everything consistently.

const STORAGE_KEY = "sr_budget";

function readSaved() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const b = raw ? JSON.parse(raw) : null;
    if (!b) return null;
    const min = Number(b.min) > 0 ? Number(b.min) : null;
    const max = Number(b.max) > 0 ? Number(b.max) : null;
    return min || max ? { min, max } : null;
  } catch {
    return null;
  }
}

// Human label for the pill: "Under $1,500", "$1,500 to $2,500", "$2,500+".
export function budgetLabel(budget) {
  const min = budget?.min;
  const max = budget?.max;
  if (!min && !max) return null;
  if (!min) return `Under $${Number(max).toLocaleString()}`;
  if (!max) return `$${Number(min).toLocaleString()}+`;
  return `$${Number(min).toLocaleString()} to $${Number(max).toLocaleString()}`;
}

// Client side check used by every mock fallback path.
export function inBudget(listing, budget) {
  if (!budget || (!budget.min && !budget.max)) return true;
  const price = Number(listing?.price ?? listing?.rent ?? 0);
  if (!price) return true; // never hide a home we cannot price
  if (budget.min && price < budget.min) return false;
  if (budget.max && price > budget.max) return false;
  return true;
}

// API params for the backend paths (GET /listings, GET /swipes/deck).
export function budgetParams(budget) {
  const params = {};
  if (budget?.min) params.price_min = budget.min;
  if (budget?.max) params.price_max = budget.max;
  return params;
}

const FiltersContext = createContext(null);

export function FiltersProvider({ children }) {
  const [budget, setBudgetState] = useState(readSaved);

  useEffect(() => {
    try {
      if (budget) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(budget));
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Storage blocked — the filter still works for this session.
    }
  }, [budget]);

  const setBudget = useCallback((next) => {
    const min = Number(next?.min) > 0 ? Number(next.min) : null;
    const max = Number(next?.max) > 0 ? Number(next.max) : null;
    if (min && max && min > max) {
      setBudgetState({ min: max, max: min }); // forgive swapped inputs
      return;
    }
    setBudgetState(min || max ? { min, max } : null);
  }, []);

  const clearBudget = useCallback(() => setBudgetState(null), []);

  const value = useMemo(() => ({ budget, setBudget, clearBudget }), [budget, setBudget, clearBudget]);

  return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>;
}

export function useFilters() {
  const ctx = useContext(FiltersContext);
  if (!ctx) {
    return { budget: null, setBudget: () => {}, clearBudget: () => {} };
  }
  return ctx;
}
